<template>
    <md-dialog
        id="donate-popup"
        :md-active.sync="isOpen"
        :md-click-outside-to-close="false"
        :md-close-on-escape="false"
        :class="{
            'donate-open': isOpen,
        }"
    >
        <div v-if="showThankyou" id="donate-thankyou">
            <md-empty-state
                md-icon="favorite"
                md-label="Thank You"
                md-description="Your payment has been processed"
            >
                <md-button class="md-primary md-raised" @click="close"
                    >Continue Watching</md-button
                >
            </md-empty-state>
        </div>
        <div v-else id="donate-form-container">
            <ValidationObserver ref="donate" v-slot="vd">
                <md-empty-state
                    v-show="showConfirm"
                    id="donate-confirm"
                    md-label="Confirm Your Donation"
                    :md-description="confirmMessage"
                >
                    <md-button
                        class="md-primary md-raised"
                        @click="onSubmitDonation"
                        >Yes, Donate Now</md-button
                    >
                    <md-button class="md-raised" @click="close"
                        >No, Cancel</md-button
                    >
                </md-empty-state>
                <form
                    v-show="!showConfirm"
                    @submit.prevent="vd.handleSubmit(showUserConfirm)"
                >
                    <button type="button" class="donate-close" @click="close">
                        <md-icon class="md-size-2x">close</md-icon>
                    </button>
                    <md-dialog-title>
                        <div class="md-title">{{ heading }}</div>
                        <div :class="{ error: true, hidden: !errorMessage }">
                            {{ errorMessage }}
                        </div>
                    </md-dialog-title>
                    <div class="spinner md-layout md-alignment-center-center">
                        <md-progress-spinner
                            v-show="sending"
                            md-mode="indeterminate"
                        />
                    </div>
                    <div :class="{ fields: true, hidden: sending }">
                        <ValidationProvider
                            ref="amountValidator"
                            v-slot="{ errors }"
                            name="amount"
                            rules="required|donateBetween:1,10000"
                            class="md-layout-item md-size-100"
                        >
                            <md-field :class="{ 'md-invalid': errors.length }">
                                <label>Amount</label>
                                <span class="md-prefix">$</span>
                                <md-input
                                    ref="amountInput"
                                    v-model="formValues.amount"
                                    v-currency="currencySettings"
                                    type="text"
                                    required
                                />
                                <span class="md-error">{{ errors[0] }}</span>
                            </md-field>
                        </ValidationProvider>
                        <ValidationProvider
                            v-if="!authed"
                            ref="emailValidator"
                            v-slot="{ errors }"
                            name="email"
                            rules="required|email"
                            class="md-layout-item md-size-100"
                        >
                            <md-field :class="{ 'md-invalid': errors.length }">
                                <label>Email</label>
                                <md-input
                                    v-model="formValues.email"
                                    type="email"
                                    required
                                />
                                <span class="md-error">{{ errors[0] }}</span>
                            </md-field>
                        </ValidationProvider>
                        <ValidationProvider
                            v-slot="{ errors }"
                            name="message"
                            rules="max:127"
                            class="md-layout-item md-size-100"
                        >
                            <md-field :class="{ 'md-invalid': errors.length }">
                                <label>Message</label>
                                <md-textarea
                                    v-model="formValues.message"
                                    rows="2"
                                    maxlength="127"
                                />
                                <span class="md-error">{{ errors[0] }}</span>
                            </md-field>
                        </ValidationProvider>

                        <PaymentForm
                            v-if="requiredFieldsFilled"
                            ref="paymentRef"
                            type="payment"
                            captcha-action="createDonationPaymentIntent"
                            :product="product"
                            :purchase-details="purchaseDetails"
                            @confirmed="onPaymentConfirmed"
                            @confirming="onConfirming"
                            @complete="onPaymentFormComplete"
                            @loading="onPaymentFormLoading"
                            @error="onPaymentError"
                        />
                    </div>

                    <md-dialog-actions>
                        <md-button
                            type="submit"
                            class="md-primary md-raised"
                            :disabled="submitDisabled"
                            >{{ buttonText }}</md-button
                        >
                    </md-dialog-actions>
                    <div
                        v-if="terms"
                        class="md-layout md-alignment-center-center"
                    >
                        <p id="donate-terms" class="md-caption">{{ terms }}</p>
                    </div>
                </form>
            </ValidationObserver>

            <div class="captcha-terms md-caption">
                This site is protected by reCAPTCHA and the Google
                <a href="https://policies.google.com/privacy">Privacy Policy</a>
                and
                <a href="https://policies.google.com/terms">Terms of Service</a>
                apply.
            </div>
        </div>
        <!-- end form container -->
    </md-dialog>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { CurrencyDirective, parse as parseCurrency } from "vue-currency-input";
import PaymentForm from "./PaymentForm.vue";

export default {
    name: "DonatePopup",
    components: {
        ValidationProvider,
        ValidationObserver,
        PaymentForm,
    },
    directives: {
        currency: CurrencyDirective,
    },
    props: {
        open: {
            type: Boolean,
            required: false,
            default: () => false,
        },
    },
    data: () => ({
        isOpen: false,
        loading: true,
        paymentFormReady: false,
        sending: false,
        authed: false,
        captcha: undefined,
        product: undefined,
        errorMessage: undefined,
        showThankyou: false,
        showConfirm: false,
        appTitle: "",
        buttonText: "Donate",
        heading: "Donate Today",
        terms: "",
        cardComplete: false,
        formValues: {
            amount: undefined,
            message: undefined,
            email: undefined,
        },
        currencySettings: {
            currency: null,
            locale: "en",
            autoDecimalMode: true,
            allowNegative: false,
            valueAsInteger: true,
            distractionFree: false,
        },
    }),
    computed: {
        confirmMessage: function () {
            return `You are about to donate $${this.formValues.amount} to ${this.appTitle}. Is that correct?`;
        },
        parsedAmount: function () {
            return parseCurrency(this.formValues.amount, this.currencySettings);
        },
        requiredFieldsFilled: function () {
            if (!this.formValues.amount) {
                return false;
            }
            return (
                this.$refs.amountValidator?.flags.invalid !== true &&
                this.$refs.emailValidator?.flags.invalid !== true
            );
        },
        submitDisabled: function () {
            return (
                this.sending ||
                this.loading ||
                !this.paymentFormReady ||
                this.$refs.amountValidator?.flags.invalid ||
                this.$refs.emailValidator?.flags.invalid
            );
        },
        purchaseDetails: function () {
            return {
                amount: this.parsedAmount,
                email: this.formValues.email,
                message: this.formValues.message,
            };
        },
    },
    watch: {
        open: function () {
            this.resetForm();
            this.isOpen = this.open;
            this.$nextTick(() => {
                if (this.open) {
                    //wait for modal animation then focus amount input
                    setTimeout(() => {
                        this.$refs.amountInput.$el.focus();
                    }, 200);
                }
            });
        },
    },
    async created() {
        const settings = await this.$appSettings;
        this.product = {
            productId: `donate_${settings.accountId}`,
            type: "donation",
        };
        this.appTitle = settings.title;
        this.buttonText = settings.donateButtonText || this.buttonText;
        this.heading = settings.donateMessage;
        this.terms = settings.donateTerms;
        this.authed = this.$gideo.authed;

        const setAuthed = () => {
            this.authed = this.$gideo.authed;
        };
        this.$gideo.bus.$on("logged-in", setAuthed);
        this.$gideo.bus.$on("logged-out", setAuthed);
    },
    mounted() {
        this.isOpen = this.open;
        this.$nextTick(() => {
            if (this.open) {
                //wait for modal animation then focus amount input
                setTimeout(() => {
                    this.$refs.amountInput.$el.focus();
                }, 200);
            }
        });
    },
    methods: {
        showUserConfirm() {
            this.showConfirm = true;
        },
        resetForm() {
            this.formValues = {
                amount: undefined,
                message: undefined,
                email: undefined,
            };
            this.showThankyou = false;
            this.showConfirm = false;
            this.captcha = undefined;
            this.errorMessage = undefined;
            this.sending = false;
            this.$refs.paymentRef?.reset();

            this.$nextTick(() => {
                this.$refs.donate && this.$refs.donate.reset();
            });
        },
        async onSubmitDonation() {
            this.showConfirm = false;
            this.$refs?.paymentRef?.submit();
        },
        onConfirming(isSending) {
            this.sending = isSending;
            if (isSending) {
                this.errorMessage = undefined;
            }
        },
        onPaymentConfirmed() {
            this.resetForm();
            this.showThankyou = true;
        },
        onPaymentFormComplete(ready) {
            this.paymentFormReady = ready;
        },
        onPaymentFormLoading(loading) {
            this.loading = loading;
        },
        onPaymentError(err) {
            this.errorMessage =
                err.userMessage ||
                "Something went wrong. Please try again later.";
        },
        close() {
            this.isOpen = false;
            this.$emit("update:open", false);
        },
    },
};
</script>

<style lang="scss">
@import "@/assets/css/_variables.scss";

.md-select-menu.md-menu-content {
    z-index: 1120;
}
#donate-popup {
    z-index: 1110;

    .error {
        padding: 1em;
        color: #ff1744;
        line-height: 1.1em;
        min-height: 3.1em;
        font-size: 12px;
        transition: 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    }
    .md-empty-state.md-theme-default.error-icon
        .md-empty-state-icon.md-icon-font {
        color: #ff5252;
    }
    .hidden {
        opacity: 0;
        z-index: -1;
    }
    .spinner {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: -1;
    }
    .fields {
        transition: 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    }
    .md-dialog-title h3 {
        font-size: 3rem;
        margin: 0;
    }
    .donate-close {
        padding: 0;
        background: transparent;
        border: none;
        width: 3.8rem;
        height: 3.8rem;
        transition:
            transform 0.3s ease 0.2s,
            opacity 0.3s ease 0.2s;
        transform: scale(0);
        cursor: pointer;
        position: absolute;
        top: 1rem;
        right: 1rem;

        &:hover {
            opacity: 0.7;
        }

        .md-icon {
            color: #333;
            width: 3.8rem;
            height: 3.8rem;
            min-width: 3.8rem;
            font-size: 3.8rem !important;
        }
    }
    &.donate-open .donate-close {
        transform: scale(1);
    }

    #donate-logo {
        max-height: 4rem;
    }
    #donate-terms {
        display: inline-block;
        max-width: 20rem;
        margin: auto;
    }

    .md-dialog-container {
        max-width: 100%;
        padding: 1rem 4rem;
        color: #333;
        background-color: #fff;
        font-size: 1.1rem;
        line-height: 1.1em;
        z-index: 1001;
        border: 0.125rem solid #fff;
        overflow: auto;
        position: relative;

        @media (min-width: 600px) {
            max-width: 90%;
        }
        @media (min-width: 1080px) {
            font-size: 1.3rem;
            min-width: 40%;
            max-width: 65%;
        }

        .md-dialog-title {
            text-align: center;
            font-size: 2.2em;
        }
        .md-dialog-actions {
            flex-direction: column;
            justify-content: center;
            .btn {
                margin: 0.5em;
            }

            @media (min-width: 600px) {
                flex-direction: row;
            }
        }
        .dialog-background {
            position: absolute;
            height: 100%;
            width: 100%;
            left: 0;
            top: 0;
            z-index: -1;
            background-repeat: no-repeat;
            background-size: cover;
        }
    }
}
</style>
